/**
 * AnimationController - Contrôleur principal des animations
 * Intégration avec les utilitaires d'animation et gestion globale
 */

import { $$, addClass, removeClass } from "../utils/dom.js";
import {
  FluidAnimator,
  scrollObserver,
  initScrollAnimations,
} from "../utils/animations.js";

export class AnimationController {
  constructor() {
    this.animatedElements = new Map();
    this.globalConfig = {
      respectMotionPreferences: true,
      defaultDuration: 300,
      defaultEasing: "ease-in-out",
      staggerDelay: 100,
    };

    this.motionReduced = false;
    this.init();
  }

  async init() {
    this.checkMotionPreferences();
    this.setupGlobalAnimations();
    this.setupIntersectionObserver();
  }

  /**
   * Vérifier les préférences de mouvement
   */
  checkMotionPreferences() {
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.motionReduced = mediaQuery.matches;

      mediaQuery.addEventListener("change", (e) => {
        this.motionReduced = e.matches;
        this.updateAnimationsForMotionPreference();
      });
    }
  }

  /**
   * Mettre à jour les animations selon les préférences
   */
  updateAnimationsForMotionPreference() {
    const rootClass = this.motionReduced ? "motion-reduced" : "motion-enabled";
    document.documentElement.className =
      document.documentElement.className.replace(
        /motion-(reduced|enabled)/g,
        "",
      ) +
      " " +
      rootClass;

    if (this.motionReduced) {
      this.disableAnimations();
    } else {
      this.enableAnimations();
    }
  }

  /**
   * Configuration des animations globales
   */
  setupGlobalAnimations() {
    // Animations au scroll
    initScrollAnimations();

    // Animations de focus
    this.setupFocusAnimations();

    // Animations de hover
    this.setupHoverAnimations();

    // Animations de page
    this.setupPageAnimations();
  }

  /**
   * Observer pour les nouveaux éléments
   */
  setupIntersectionObserver() {
    // Réutiliser l'observer existant depuis utils/animations.js
    const elementsToAnimate = $$("[data-animate], .animate-on-scroll");

    elementsToAnimate.forEach((element) => {
      const animationType = element.dataset.animate || "fadeIn";
      const delay = parseInt(element.dataset.animateDelay) || 0;
      const duration =
        parseInt(element.dataset.animateDuration) ||
        this.globalConfig.defaultDuration;

      scrollObserver.observe(element, {
        onEnter: (el) => {
          setTimeout(() => {
            this.animateElement(el, animationType, { duration });
          }, delay);
        },
      });
    });
  }

  /**
   * Animations de focus améliorées
   */
  setupFocusAnimations() {
    const focusableElements = $$(
      'button, a, input, textarea, select, [tabindex]:not([tabindex="-1"])',
    );

    focusableElements.forEach((element) => {
      element.addEventListener("focus", () => {
        if (!this.motionReduced) {
          addClass(element, "focus-animated");
          FluidAnimator.animate(element, "scaleIn", { duration: 150 });
        }
      });

      element.addEventListener("blur", () => {
        removeClass(element, "focus-animated");
      });
    });
  }

  /**
   * Animations de hover fluides
   */
  setupHoverAnimations() {
    const hoverElements = $$("[data-hover-animate]");

    hoverElements.forEach((element) => {
      const hoverType = element.dataset.hoverAnimate || "lift";

      element.addEventListener("mouseenter", () => {
        if (!this.motionReduced) {
          this.applyHoverEffect(element, hoverType, "enter");
        }
      });

      element.addEventListener("mouseleave", () => {
        if (!this.motionReduced) {
          this.applyHoverEffect(element, hoverType, "leave");
        }
      });
    });
  }

  /**
   * Appliquer les effets de hover
   */
  applyHoverEffect(element, type, phase) {
    switch (type) {
      case "lift":
        if (phase === "enter") {
          element.style.transform = "translateY(-5px)";
          element.style.boxShadow = "0 10px 25px rgba(0, 0, 0, 0.15)";
        } else {
          element.style.transform = "";
          element.style.boxShadow = "";
        }
        break;

      case "scale":
        if (phase === "enter") {
          element.style.transform = "scale(1.05)";
        } else {
          element.style.transform = "";
        }
        break;

      case "rotate":
        if (phase === "enter") {
          element.style.transform = "rotate(3deg)";
        } else {
          element.style.transform = "";
        }
        break;

      case "glow":
        if (phase === "enter") {
          element.style.boxShadow = "0 0 20px rgba(0, 122, 204, 0.4)";
        } else {
          element.style.boxShadow = "";
        }
        break;
    }
  }

  /**
   * Animations de transition de page
   */
  setupPageAnimations() {
    // Animation d'entrée de page
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        this.animatePageIn();
      });
    } else {
      this.animatePageIn();
    }

    // Animation de sortie de page (liens internes)
    // DÉSACTIVÉE - provoquait des problèmes avec target="_blank" et écran blanc
    // Les liens fonctionnent maintenant normalement sans interception
  }

  /**
   * Animation d'entrée de page
   * DÉSACTIVÉE - Pas d'animation au chargement initial pour éviter le scintillement
   */
  animatePageIn() {
    // Animation désactivée pour le chargement initial
    // Les éléments apparaissent instantanément
    addClass(document.body, "page-entered");
    return;

    /* Code original commenté
    if (this.motionReduced) return;

    addClass(document.body, 'page-entering');

    // Animer les éléments principaux
    const mainElements = $$('header, main, footer');
    FluidAnimator.stagger(mainElements, 'fadeIn', 200, { duration: 600 })
      .then(() => {
        removeClass(document.body, 'page-entering');
        addClass(document.body, 'page-entered');
      });
    */
  }

  /**
   * Animation de sortie de page
   * DÉSACTIVÉE - Provoque un écran blanc lors de la navigation
   */
  animatePageOut() {
    // Animation désactivée - navigation instantanée
    return Promise.resolve();
  }

  /**
   * Vérifier si un lien est interne
   */
  isInternalLink(link) {
    const href = link.getAttribute("href");
    if (!href) return false;

    // Liens relatifs
    if (
      href.startsWith("/") ||
      href.startsWith("./") ||
      href.startsWith("../")
    ) {
      return true;
    }

    // Liens absolus vers le même domaine
    try {
      const url = new URL(href, window.location.href);
      return url.hostname === window.location.hostname;
    } catch {
      return false;
    }
  }

  /**
   * Animer un élément spécifique
   */
  async animateElement(element, animationType, options = {}) {
    if (this.motionReduced && options.respectMotion !== false) {
      return Promise.resolve(element);
    }

    const config = {
      duration: this.globalConfig.defaultDuration,
      easing: this.globalConfig.defaultEasing,
      ...options,
    };

    return FluidAnimator.animate(element, animationType, config);
  }

  /**
   * Animer un groupe d'éléments avec décalage
   */
  async animateGroup(elements, animationType, options = {}) {
    if (this.motionReduced && options.respectMotion !== false) {
      return Promise.resolve(elements);
    }

    const config = {
      duration: this.globalConfig.defaultDuration,
      delay: this.globalConfig.staggerDelay,
      ...options,
    };

    return FluidAnimator.stagger(elements, animationType, config.delay, config);
  }

  /**
   * Désactiver toutes les animations
   */
  disableAnimations() {
    addClass(document.documentElement, "motion-reduced");

    // Arrêter les animations CSS
    const style = document.createElement("style");
    style.id = "motion-reduced-styles";
    style.textContent = `
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
    `;
    document.head.appendChild(style);

    // Arrêter les animations Web API
    $$("*").forEach((element) => {
      const animations = element.getAnimations ? element.getAnimations() : [];
      animations.forEach((animation) => animation.cancel());
    });
  }

  /**
   * Réactiver les animations
   */
  enableAnimations() {
    removeClass(document.documentElement, "motion-reduced");

    // Supprimer les styles de réduction de mouvement
    const style = document.querySelector("#motion-reduced-styles");
    if (style) {
      style.remove();
    }
  }

  /**
   * API publique pour les composants
   */

  // Enregistrer un élément pour animation
  register(element, config = {}) {
    const id = element.dataset.animateId || this.generateId();
    element.dataset.animateId = id;

    this.animatedElements.set(id, {
      element,
      config: { ...this.globalConfig, ...config },
    });

    return id;
  }

  // Désenregistrer un élément
  unregister(elementOrId) {
    const id =
      typeof elementOrId === "string"
        ? elementOrId
        : elementOrId.dataset.animateId;
    if (id) {
      this.animatedElements.delete(id);
    }
  }

  // Générer un ID unique
  generateId() {
    return "anim_" + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Attacher au contexte Drupal
   */
  attach(context) {
    // Réinitialiser les animations pour le nouveau contenu
    this.setupIntersectionObserver();
    this.setupFocusAnimations();
    this.setupHoverAnimations();
  }
}
